import { Typography } from '@mui/material'

import {
  ConsoleItemsConnectionFragment,
  ConsoleMissionBoxFragment,
  Maybe,
  MissionType,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { Place, Prediction } from '@/lib/googleMap'

import {
  FileUploadInfo,
  MissionEditForm,
  MissionEditFormProps,
} from './MissionEditForm'

export * from './fragments'

export type State = {
  artistId: string
  progress: number
  mission: Maybe<ConsoleMissionBoxFragment>
  items: Maybe<ConsoleItemsConnectionFragment>
  loading: boolean
  hasNextPage: boolean
  predictions: Prediction[]
  place: Maybe<Place>
  gpsClientInfo: MissionEditFormProps['gpsClientInfo']
}

type HeadingTextCellProps = {
  missionType: MissionType
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: MissionEditFormProps['onSubmit']
  onDrop: FileUploadInfo['onDrop']
  onFetchMore: () => Promise<void>
  onSearch: (value: { keyword: string }) => Promise<void>
  onPlaceSearch: (value: { keyword: string }) => Promise<void>
  onSelectPlace: (value: {
    placeId: string
    map: google.maps.Map
  }) => Promise<void>
  onRequestGPSPermission: () => void
  removePlace: () => void
}

export const ConsoleMissionEditTemplate: Template<State, Action> = ({
  state,
  onSubmit,
  onDrop,
  onSearch,
  onFetchMore,
  onPlaceSearch,
  onSelectPlace,
  onRequestGPSPermission,
  removePlace,
}) => {
  const { t } = useTranslation('consoleMissionEdit')
  const { routerNames, routerPages } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleMissionEdit} />

      <ConsoleBaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleMissions,
          title: t('title'),
        }}
      >
        {state.mission?.type && (
          <HeadingTextCell missionType={state.mission.type} />
        )}
        {state.mission && (
          <MissionEditForm
            mission={state.mission}
            onSubmit={onSubmit}
            predictions={state.predictions}
            place={state.place}
            onSelectPlace={onSelectPlace}
            onPlaceSearch={onPlaceSearch}
            fileUploadInfo={{
              onDrop,
              progress: state.progress,
            }}
            gpsClientInfo={state.gpsClientInfo}
            artistId={state.artistId}
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            items={state.items}
            onFetchMore={onFetchMore}
            onSearch={onSearch}
            onRequestGPSPermission={onRequestGPSPermission}
            removePlace={removePlace}
          />
        )}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const HeadingTextCell: Cell<HeadingTextCellProps> = ({ missionType }) => {
  const { t } = useTranslation('consoleMissionEdit')
  return (
    <CellFrame sx={{ py: 3, px: 5, mt: 2 }}>
      <Typography variant="h3">{t(`${missionType}MissionEdit`)}</Typography>
    </CellFrame>
  )
}
